import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Filter, MapPin, RefreshCw, Search } from 'lucide-react'
import maplibregl from 'maplibre-gl/dist/maplibre-gl-csp'
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-csp-worker.js?url'
import 'maplibre-gl/dist/maplibre-gl.css'
import L from 'leaflet'
import { MapContainer, Marker, TileLayer } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import { usePermissions } from '../../hooks/usePermissions'
import { clinicaService } from '../../services/clinicaService'
import { suscripcionClinicaService } from '../../services/suscripcionClinicaService'
import { veterinarioService } from '../../services/veterinarioService'
import LoadingState from '../../components/ui/LoadingState'
import EmptyState from '../../components/ui/EmptyState'
import { CARTO_ATTRIBUTION, CARTO_LIGHT_TILE_URL, CARTO_TILE_SUBDOMAINS } from '../../constants/mapTiles'

const VECTOR_STYLE_URL = 'https://tiles.openfreemap.org/styles/bright'
const VECTOR_STYLE_FALLBACK_URL = 'https://demotiles.maplibre.org/style.json'
/** Raster Carto (mismo proveedor que Leaflet) si MapLibre cae a estilo raster */
const RASTER_STYLE_FALLBACK = {
  version: 8,
  sources: {
    carto: {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
        'https://b.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
        'https://c.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
        'https://d.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: CARTO_ATTRIBUTION,
    },
  },
  layers: [
    {
      id: 'carto',
      type: 'raster',
      source: 'carto',
    },
  ],
}
const MONTEVIDEO_CENTER = [-56.1645, -34.9011]
const EMOJIS = ['🏥', '🐾', '💊', '🩺', '🐶', '🐱']

if (typeof maplibregl?.setWorkerUrl === 'function') {
  maplibregl.setWorkerUrl(maplibreWorkerUrl)
}

function extractArray(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.data)) return payload.data.data
  return []
}

function parseNumber(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function parseCoordinate(value) {
  if (value == null) return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const parsed = Number.parseFloat(String(value).trim().replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : null
}

function getClinicaId(item = {}) {
  return item.id_clinica || item.idClinica || item.id
}

function isActiveSubscription(status) {
  const normalized = String(status || '').toUpperCase().trim()
  return normalized === 'ACTIVA' || normalized === 'PENDIENTE_APROBACION'
}

function getClinicCoordinates(clinic = {}) {
  const latCandidates = [
    clinic.latitud,
    clinic.latitude,
    clinic.lat,
    clinic.coordenadas?.latitud,
    clinic.coordenadas?.latitude,
    clinic.coordenadas?.lat,
    clinic.ubicacion?.latitud,
    clinic.ubicacion?.latitude,
    clinic.ubicacion?.lat,
  ]

  const lngCandidates = [
    clinic.longitud,
    clinic.longitude,
    clinic.lng,
    clinic.lon,
    clinic.coordenadas?.longitud,
    clinic.coordenadas?.longitude,
    clinic.coordenadas?.lng,
    clinic.coordenadas?.lon,
    clinic.ubicacion?.longitud,
    clinic.ubicacion?.longitude,
    clinic.ubicacion?.lng,
    clinic.ubicacion?.lon,
  ]

  const geoCoords = clinic.ubicacion?.coordinates || clinic.coordenadas?.coordinates
  if (Array.isArray(geoCoords) && geoCoords.length >= 2) {
    const lng = parseCoordinate(geoCoords[0])
    const lat = parseCoordinate(geoCoords[1])
    if (lat != null && lng != null) return { lat, lng }
  }

  const lat = latCandidates.map(parseCoordinate).find((v) => v != null)
  const lng = lngCandidates.map(parseCoordinate).find((v) => v != null)
  if (lat == null || lng == null) return null
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null
  return { lat, lng }
}

function stars(rating) {
  return '★'.repeat(Math.max(1, Math.round(rating)))
}

/** Alineado con artemis-map.html: activa / destacada / en revisión */
function getPinState(clinic) {
  const sub = clinic.suscripcion
  const estado = String(sub?.estado || '').toUpperCase().trim()
  if (estado === 'PENDIENTE_APROBACION') return 'review'
  if (String(clinic.plan || '').toUpperCase() === 'PREMIUM') return 'featured'
  return 'active'
}

/** HTML para markers MapLibre / Leaflet DivIcon — mismo look que artemis-map.html */
function buildPinHtml(pinState, selected) {
  const size = selected ? 52 : pinState === 'review' ? 40 : 44
  const bg = pinState === 'review' ? '#aab4c0' : pinState === 'featured' ? '#00c896' : '#0f1623'
  const shadow = selected ? '0 4px 16px rgba(15,22,35,0.35)' : '0 3px 10px rgba(0,0,0,0.2)'
  const icon =
    pinState === 'featured'
      ? `<svg width="${Math.round(size * 0.5)}" height="${Math.round(size * 0.5)}" viewBox="0 0 24 24" fill="none" stroke="#0f1623" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>`
      : pinState === 'review'
        ? `<svg width="${Math.round(size * 0.48)}" height="${Math.round(size * 0.48)}" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>`
        : `<svg width="${Math.round(size * 0.55)}" height="${Math.round(size * 0.55)}" viewBox="0 0 24 24" fill="none" stroke="#e8edf5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>`
  return `<div class="artemis-pin-bubble" style="width:${size}px;height:${size}px;border-radius:50%;background:${bg};border:3px solid #fff;box-shadow:${shadow};display:flex;align-items:center;justify-content:center;cursor:pointer">${icon}</div>`
}

function createLeafletPinIcon(clinic, selected) {
  const state = clinic.pinState || getPinState(clinic)
  const html = buildPinHtml(state, selected)
  const size = selected ? 52 : state === 'review' ? 40 : 44
  return L.divIcon({
    className: 'artemis-leaflet-pin',
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

function filterMatch(clinic, activeFilters, searchTerm) {
  if (activeFilters.has('suscrito') && !clinic.suscrito) return false
  if (activeFilters.has('premium') && clinic.plan !== 'PREMIUM') return false
  if (activeFilters.has('urgencias') && !clinic.urgencias) return false
  if (activeFilters.has('cerca') && clinic.distancia >= 2) return false
  if (activeFilters.has('rating') && clinic.rating < 4.5) return false
  const term = searchTerm.trim().toLowerCase()
  if (!term) return true
  return `${clinic.nombre} ${clinic.dir} ${clinic.pais || ''}`.toLowerCase().includes(term)
}

function normalizeClinic(item = {}, idx = 0, subMap = new Map()) {
  const id = getClinicaId(item)
  const sub = subMap.get(String(id))
  return withPinState({
    id,
    nombre: item.nombre || item.nombre_clinica || item.nombreClinica || `Clínica ${id || idx + 1}`,
    dir: item.direccion || item.direccion_completa || item.direccionCompleta || item.ciudad || 'Sin dirección',
    plan: String(item.plan_publicidad || item.planPublicidad || item.plan || 'BASICO').toUpperCase(),
    rating: parseNumber(item.rating || item.calificacion || item.promedio_rating, 4.4),
    urgencias: Boolean(item.urgencias_24h || item.urgencias24h || item.acepta_emergencias_auto || item.aceptaEmergenciasAuto),
    distancia: parseNumber(item.distancia || item.distancia_km || item.distanciaKm, 1 + idx * 0.65),
    emoji: item.emoji || EMOJIS[idx % EMOJIS.length],
    suscripcion: sub || null,
    suscrito: !!sub && isActiveSubscription(sub.estado),
    coords: getClinicCoordinates(item),
  })
}

function withPinState(base) {
  return { ...base, pinState: getPinState(base) }
}

export default function ClinicasMobileVectorExact() {
  const navigate = useNavigate()
  const { isDueno } = usePermissions()

  const mapContainerRef = useRef(null)
  const mapRef = useRef(null)
  const markersRef = useRef([])
  const styleFallbackStepRef = useRef(0)

  const [mapReady, setMapReady] = useState(false)
  const [mapError, setMapError] = useState('')
  // Priorizamos modo compatible para mejorar confiabilidad en móviles.
  const [useLeafletFallback, setUseLeafletFallback] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busyClinicId, setBusyClinicId] = useState('')

  const [clinicas, setClinicas] = useState([])
  const [selectedClinicId, setSelectedClinicId] = useState(null)
  const [selectedClinicVets, setSelectedClinicVets] = useState([])
  const [vetsLoading, setVetsLoading] = useState(false)

  const [searchTerm, setSearchTerm] = useState('')
  const [sheetExpanded, setSheetExpanded] = useState(true)
  const [sheetDragY, setSheetDragY] = useState(0)
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)
  const [activeFilters, setActiveFilters] = useState(new Set())
  const [filterPais, setFilterPais] = useState('')
  const dragStateRef = useRef({ active: false, pointerId: null, startY: 0 })
  const suppressSheetToggleClickRef = useRef(false)

  const loadClinicas = useCallback(async () => {
    try {
      setLoading(true)
      setError('')

      const [clinicasRes, subsRes] = await Promise.all([
        clinicaService.getAll({ per_page: 300, ...(filterPais ? { pais: filterPais } : {}) }).catch(() => null),
        suscripcionClinicaService.getAll().catch(() => null),
      ])

      const subs = extractArray(subsRes)
      const subMap = new Map()
      subs.forEach((sub) => {
        const clinicId = getClinicaId(sub?.clinica || {}) || sub.id_clinica || sub.idClinica
        if (clinicId) subMap.set(String(clinicId), sub)
      })

      let clinicsRaw = extractArray(clinicasRes)
      if (clinicsRaw.length === 0) {
        const availableRes = await suscripcionClinicaService.getClinicasDisponibles().catch(() => null)
        clinicsRaw = extractArray(availableRes)
      }
      if (clinicsRaw.length === 0) clinicsRaw = subs.map((s) => s.clinica).filter(Boolean)

      const normalized = clinicsRaw
        .map((item, idx) => normalizeClinic(item, idx, subMap))
        .filter((item) => item.id != null)

      setClinicas(normalized)
      setSelectedClinicId((prev) => {
        if (prev && normalized.some((c) => String(c.id) === String(prev))) return prev
        return normalized.find((c) => c.suscrito)?.id || normalized[0]?.id || null
      })
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'No se pudieron cargar las clínicas')
      setClinicas([])
    } finally {
      setLoading(false)
    }
  }, [filterPais])

  useEffect(() => {
    loadClinicas()
  }, [loadClinicas])

  useEffect(() => {
    if (!selectedClinicId) {
      setSelectedClinicVets([])
      return
    }
    let cancelled = false
    const loadVets = async () => {
      try {
        setVetsLoading(true)
        const byClinic = await veterinarioService.getAll({ id_clinica: selectedClinicId, per_page: 200 }).catch(() => null)
        let vets = extractArray(byClinic)
        if (vets.length === 0) {
          const allRes = await veterinarioService.getAll({ per_page: 200 }).catch(() => null)
          vets = extractArray(allRes).filter((item) => {
            const cid = item.id_clinica || item.idClinica || item.clinica?.id_clinica || item.clinica?.idClinica
            return String(cid) === String(selectedClinicId)
          })
        }
        if (!cancelled) setSelectedClinicVets(vets)
      } finally {
        if (!cancelled) setVetsLoading(false)
      }
    }
    loadVets()
    return () => {
      cancelled = true
    }
  }, [selectedClinicId])

  const selectedClinic = useMemo(() => {
    return clinicas.find((item) => String(item.id) === String(selectedClinicId)) || null
  }, [clinicas, selectedClinicId])

  const subscribedClinic = useMemo(() => clinicas.find((item) => item.suscrito) || null, [clinicas])
  /** Sin clínica suscrita: solo mapa + pines + búsqueda. Con suscripción: panel de detalle sobre el mapa. */
  const exploreMode = !subscribedClinic

  const filteredClinics = useMemo(() => {
    return clinicas.filter((item) => filterMatch(item, activeFilters, searchTerm))
  }, [clinicas, activeFilters, searchTerm])

  useEffect(() => {
    if (filteredClinics.length === 0) return
    if (!selectedClinicId || !filteredClinics.some((item) => String(item.id) === String(selectedClinicId))) {
      setSelectedClinicId(filteredClinics[0].id)
    }
  }, [filteredClinics, selectedClinicId])

  const clinicsForMap = useMemo(() => {
    return filteredClinics
      .filter((item) => !!item.coords)
      .map((item) => ({
        ...item,
        lat: item.coords.lat,
        lng: item.coords.lng,
      }))
  }, [filteredClinics])

  const selectedMapPoint = useMemo(() => {
    return clinicsForMap.find((item) => String(item.id) === String(selectedClinicId)) || null
  }, [clinicsForMap, selectedClinicId])

  const mapCenter = useMemo(() => {
    if (selectedMapPoint) return [selectedMapPoint.lat, selectedMapPoint.lng]
    if (clinicsForMap.length > 0) {
      const avgLat = clinicsForMap.reduce((sum, c) => sum + c.lat, 0) / clinicsForMap.length
      const avgLng = clinicsForMap.reduce((sum, c) => sum + c.lng, 0) / clinicsForMap.length
      return [avgLat, avgLng]
    }
    return [MONTEVIDEO_CENTER[1], MONTEVIDEO_CENTER[0]]
  }, [clinicsForMap, selectedMapPoint])

  const pills = useMemo(() => {
    const labels = {
      suscrito: '✓ Suscritas',
      premium: '★ Premium',
      urgencias: '⚡ 24h',
      cerca: '📍 Cerca',
      rating: '⭐ Top rated',
    }
    const fromSet = Array.from(activeFilters).map((key) => ({ key, label: labels[key] || key }))
    if (filterPais) return [{ key: 'pais', label: `🌍 ${filterPais}` }, ...fromSet]
    return fromSet
  }, [activeFilters, filterPais])

  const toggleFilter = (key) => {
    setActiveFilters((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const clearFilters = () => {
    setActiveFilters(new Set())
    setFilterPais('')
  }

  const openDirections = useCallback((clinic) => {
    const c = clinic?.coords || (clinic?.lat != null && clinic?.lng != null ? { lat: clinic.lat, lng: clinic.lng } : null)
    if (!c) return
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${c.lat},${c.lng}`,
      '_blank',
      'noopener,noreferrer'
    )
  }, [])

  const openClinicaDetail = useCallback(
    (clinic) => {
      if (!clinic?.id) return
      if (isDueno) navigate(`/suscripciones-clinicas/${clinic.id}`)
      else navigate('/clinicas')
    },
    [isDueno, navigate]
  )

  const removePill = (key) => {
    if (key === 'pais') {
      setFilterPais('')
      return
    }
    setActiveFilters((prev) => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
  }

  const handleSubscriptionToggle = async (clinicId) => {
    if (!isDueno) return
    const clinic = clinicas.find((item) => String(item.id) === String(clinicId))
    if (!clinic) return

    try {
      setBusyClinicId(String(clinicId))
      setError('')
      const subId = clinic.suscripcion?.id_suscripcion || clinic.suscripcion?.idSuscripcion
      if (subId) await suscripcionClinicaService.unsubscribe(subId)
      else await suscripcionClinicaService.subscribe(clinicId)
      await loadClinicas()
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'No se pudo actualizar la suscripción')
    } finally {
      setBusyClinicId('')
    }
  }

  const releaseSheetHandlePointer = (event) => {
    const el = event.currentTarget
    try {
      if (typeof el.releasePointerCapture === 'function') el.releasePointerCapture(event.pointerId)
    } catch {
      /* ignore */
    }
  }

  const handleSheetPointerDown = (event) => {
    const el = event.currentTarget
    try {
      if (typeof el.setPointerCapture === 'function') el.setPointerCapture(event.pointerId)
    } catch {
      /* ignore */
    }
    dragStateRef.current = {
      active: true,
      pointerId: event.pointerId,
      startY: event.clientY,
    }
  }

  const handleSheetPointerMove = (event) => {
    const state = dragStateRef.current
    if (!state.active || event.pointerId !== state.pointerId) return
    const deltaY = event.clientY - state.startY
    // Si está expandido solo permitimos arrastrar hacia abajo; si está colapsado, hacia arriba.
    if (sheetExpanded) {
      setSheetDragY(Math.max(0, deltaY))
    } else {
      setSheetDragY(Math.min(0, deltaY))
    }
  }

  const finishSheetDrag = (event) => {
    const state = dragStateRef.current
    if (!state.active || event.pointerId !== state.pointerId) return
    releaseSheetHandlePointer(event)
    const deltaY = event.clientY - state.startY
    if (Math.abs(deltaY) > 12) suppressSheetToggleClickRef.current = true
    if (deltaY > 40) setSheetExpanded(false)
    if (deltaY < -40) setSheetExpanded(true)
    dragStateRef.current = { active: false, pointerId: null, startY: 0 }
    setSheetDragY(0)
  }

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current || useLeafletFallback) return

    if (typeof maplibregl.supported === 'function' && !maplibregl.supported({ failIfMajorPerformanceCaveat: false })) {
      setUseLeafletFallback(true)
      setMapError('El dispositivo no soporta mapa vectorial WebGL, usando modo compatible.')
      return
    }

    let initTimeoutId = null
    let localMap = null
    const applyNextStyleFallback = () => {
      if (!localMap) return
      if (styleFallbackStepRef.current === 0) {
        styleFallbackStepRef.current = 1
        localMap.setStyle(VECTOR_STYLE_FALLBACK_URL)
        return
      }
      if (styleFallbackStepRef.current === 1) {
        styleFallbackStepRef.current = 2
        localMap.setStyle(RASTER_STYLE_FALLBACK)
        return
      }
      setUseLeafletFallback(true)
      setMapError('No se pudo cargar mapa vectorial, usando modo compatible.')
    }

    try {
      localMap = new maplibregl.Map({
        container: mapContainerRef.current,
        style: VECTOR_STYLE_URL,
        center: MONTEVIDEO_CENTER,
        zoom: 12,
        attributionControl: true,
      })
    } catch (err) {
      setUseLeafletFallback(true)
      setMapError('No se pudo inicializar mapa vectorial, usando modo compatible.')
      return
    }

    localMap.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right')

    localMap.on('load', () => {
      setMapReady(true)
      setMapError('')
    })

    localMap.on('error', (event) => {
      const message = String(event?.error?.message || '').toLowerCase()
      if (!message) return
      if (/worker|webgl|context|blob|csp/.test(message)) {
        setUseLeafletFallback(true)
        setMapError('Mapa vectorial bloqueado en este entorno, usando modo compatible.')
        return
      }
      if (/style|sprite|glyph|source|tile|fetch|network|403|404|401/.test(message)) {
        applyNextStyleFallback()
        return
      }
      setMapError('No se pudo cargar completamente el mapa vectorial.')
    })

    initTimeoutId = window.setTimeout(() => {
      if (!mapReady) {
        applyNextStyleFallback()
      }
    }, 6500)

    mapRef.current = localMap

    return () => {
      if (initTimeoutId) window.clearTimeout(initTimeoutId)
      markersRef.current.forEach((marker) => marker.remove())
      markersRef.current = []
      mapRef.current?.remove()
      mapRef.current = null
    }
  }, [mapReady, useLeafletFallback])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady || useLeafletFallback) return

    markersRef.current.forEach((marker) => marker.remove())
    markersRef.current = []

    clinicsForMap.forEach((clinic) => {
      const markerEl = document.createElement('button')
      markerEl.type = 'button'
      markerEl.className = `artemis-ml-pin ${String(clinic.id) === String(selectedClinicId) ? 'selected' : ''}`
      markerEl.innerHTML = buildPinHtml(clinic.pinState, String(clinic.id) === String(selectedClinicId))
      markerEl.addEventListener('click', () => {
        setSelectedClinicId(clinic.id)
        if (subscribedClinic) setSheetExpanded(true)
      })

      const marker = new maplibregl.Marker({ element: markerEl, anchor: 'center' })
        .setLngLat([clinic.lng, clinic.lat])
        .addTo(map)

      markersRef.current.push(marker)
    })

    if (!selectedMapPoint && clinicsForMap.length > 0) {
      if (clinicsForMap.length === 1) {
        map.easeTo({
          center: [clinicsForMap[0].lng, clinicsForMap[0].lat],
          zoom: 13.5,
          duration: 700,
        })
      } else {
        const bounds = clinicsForMap.reduce((acc, clinic) => {
          if (!acc) return new maplibregl.LngLatBounds([clinic.lng, clinic.lat], [clinic.lng, clinic.lat])
          acc.extend([clinic.lng, clinic.lat])
          return acc
        }, null)
        if (bounds) {
          map.fitBounds(bounds, {
            padding: {
              top: 120,
              right: 40,
              bottom: subscribedClinic ? 260 : 90,
              left: 40,
            },
            maxZoom: 13.5,
            duration: 700,
          })
        }
      }
    }
  }, [clinicsForMap, mapReady, selectedClinicId, selectedMapPoint, useLeafletFallback, subscribedClinic])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady || !selectedMapPoint || useLeafletFallback) return
    map.easeTo({
      center: [selectedMapPoint.lng, selectedMapPoint.lat],
      zoom: 13.8,
      duration: 650,
    })
  }, [mapReady, selectedMapPoint, useLeafletFallback])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady || useLeafletFallback) return undefined
    const resizeMap = () => map.resize()
    const timeout = window.setTimeout(resizeMap, 360)
    window.addEventListener('resize', resizeMap)
    return () => {
      window.clearTimeout(timeout)
      window.removeEventListener('resize', resizeMap)
    }
  }, [mapReady, sheetExpanded, filterPanelOpen, useLeafletFallback])

  if (loading) return <LoadingState message="Cargando clínicas..." />

  if (clinicas.length === 0) {
    return (
      <div className="px-4 py-6">
        <EmptyState
          icon={Filter}
          title="No hay clínicas disponibles"
          message={error || 'No encontramos clínicas para mostrar.'}
        />
      </div>
    )
  }

  const vetsToDisplay = selectedClinicVets.slice(0, 3)

  return (
    <div
      className="flex flex-col h-full min-h-0 w-full bg-[#e8edf5] font-[family-name:var(--font-dm)]"
      style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      <style>{`
        /* Leaflet usa capas 200–700; la UI debe ir en una capa superior (como artemis-map.html) */
        .map-view{position:relative;background:#eef0f3;overflow:hidden;min-height:0;isolation:isolate}
        .vector-map{position:absolute;inset:0;z-index:0}
        .map-view .leaflet-container{z-index:0!important}
        .map-view .leaflet-pane{z-index:auto!important}
        .map-view .leaflet-tile-pane{z-index:1!important}
        .map-view .leaflet-overlay-pane{z-index:2!important}
        .map-view .leaflet-shadow-pane{z-index:3!important}
        .map-view .leaflet-marker-pane{z-index:4!important}
        .map-view .leaflet-tooltip-pane{z-index:5!important}
        .map-view .leaflet-popup-pane{z-index:6!important}
        .map-view .leaflet-control-container{z-index:100!important}
        .map-ui-stack{z-index:1000;pointer-events:none}
        .map-ui-stack .map-ui-interactive{pointer-events:auto}
        .map-search{position:absolute;top:12px;left:12px;right:12px;display:flex;align-items:center;gap:8px}
        .map-srch-box{flex:1;background:#fff;border-radius:14px;padding:10px 14px;display:flex;align-items:center;gap:8px;box-shadow:0 2px 12px rgba(0,0,0,.08);font-size:13px;color:#7a8fa6}
        .map-filter-btn{width:40px;height:40px;background:#fff;border-radius:12px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 12px rgba(0,0,0,.08);cursor:pointer;flex-shrink:0;border:none;position:relative;transition:background .2s;color:#1a2535}
        .map-filter-btn.active{background:#0f1623;color:#00c896}
        .map-filter-badge{position:absolute;top:-4px;right:-4px;width:16px;height:16px;background:#00c896;border-radius:50%;border:2px solid #fff;font-size:9px;font-weight:700;color:#0f1623;display:flex;align-items:center;justify-content:center}
        .filter-panel{position:absolute;top:58px;right:12px;background:#fff;border-radius:18px;box-shadow:0 8px 28px rgba(15,22,35,.12);padding:14px;width:230px;display:none}
        .filter-panel.open{display:block}
        .fp-title{font-size:11px;font-weight:700;color:#7a8fa6;text-transform:uppercase;letter-spacing:.5px;margin-bottom:10px}
        .fp-chips{display:flex;flex-wrap:wrap;gap:7px;margin-bottom:12px}
        .fp-divider{height:1px;background:#e8eef4;margin:10px 0}
        .fp-apply{width:100%;background:#0f1623;color:#00c896;border:none;border-radius:11px;padding:10px;font-size:13px;font-weight:700;cursor:pointer}
        .fp-clear{width:100%;background:none;border:none;color:#aab4c0;font-size:12px;cursor:pointer;margin-top:6px;padding:4px}
        .filters-row{display:flex;gap:7px;padding:0 14px 10px;overflow-x:auto}
        .fchip{display:flex;align-items:center;gap:5px;padding:7px 13px;border-radius:20px;font-size:12px;font-weight:600;cursor:pointer;white-space:nowrap;border:1.5px solid #e0e8f0;background:#fff;color:#1a2535}
        .fchip.on{background:#0f1623;color:#00c896;border-color:#0f1623}
        .fchip.on-blue{background:#0f1623;color:#fff;border-color:#0f1623}
        .fchip.on-orange{background:#00c896;color:#0f1623;border-color:#00c896}
        .map-legend{position:absolute;top:72px;right:12px;background:#fff;border-radius:12px;padding:10px 12px;box-shadow:0 2px 12px rgba(0,0,0,.1);max-width:min(200px,42vw)}
        .map-legend-item{display:flex;align-items:center;gap:7px;font-size:10px;color:#1a2535;margin-bottom:5px;font-weight:500}
        .map-legend-item:last-child{margin-bottom:0}
        .map-legend-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0}
        .map-floating-popup{position:absolute;left:50%;bottom:218px;transform:translateX(-50%);width:min(calc(100% - 28px),320px);background:#fff;border-radius:18px;padding:16px 20px;text-align:center;box-shadow:0 8px 32px rgba(0,0,0,.14)}
        .map-floating-popup.popup-explore{bottom:88px}
        .map-floating-popup .popup-name{font-size:16px;font-weight:600;color:#1a2535;margin-bottom:3px}
        .map-floating-popup .popup-meta{font-size:12px;color:#7a8fa6;display:flex;align-items:center;gap:4px;justify-content:center;margin-bottom:12px;flex-wrap:wrap}
        .map-floating-popup .popup-btns{display:flex;gap:7px;justify-content:center}
        .map-floating-popup .popup-btn{font-size:11px;font-weight:600;padding:6px 14px;border-radius:20px;cursor:pointer;border:none;font-family:inherit}
        .map-floating-popup .popup-btn--primary{background:#0f1623;color:#00c896}
        .map-floating-popup .popup-btn--ghost{background:#f0f4f8;color:#1a2535}
        .map-floating-popup .popup-arrow{position:absolute;bottom:-9px;left:50%;transform:translateX(-50%);width:0;height:0;border-left:9px solid transparent;border-right:9px solid transparent;border-top:9px solid #fff;filter:drop-shadow(0 2px 2px rgba(0,0,0,.06))}
        .map-bottom-mini-cards{position:absolute;bottom:118px;left:0;right:0;display:flex;gap:10px;padding:12px 14px 10px;overflow-x:auto;-webkit-overflow-scrolling:touch;background:linear-gradient(to top,rgba(238,240,243,.92),rgba(238,240,243,0));scrollbar-width:none}
        .map-bottom-mini-cards::-webkit-scrollbar{display:none}
        .mini-card{flex-shrink:0;background:#fff;border-radius:18px;padding:12px 10px 10px;width:84px;display:flex;flex-direction:column;align-items:center;gap:7px;box-shadow:0 2px 12px rgba(0,0,0,.09);cursor:pointer;transition:transform .15s;border:2px solid transparent}
        .mini-card:hover{transform:translateY(-2px)}
        .mini-card.active{box-shadow:0 4px 16px rgba(0,0,0,.14);border-color:#00c896}
        .mini-card-icon{width:52px;height:52px;border-radius:15px;display:flex;align-items:center;justify-content:center;font-size:22px}
        .mini-card-name{font-size:9px;color:#1a2535;font-weight:600;text-align:center;line-height:1.3;max-width:100%;overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical}
        .mini-card-dist{font-size:9px;color:#7a8fa6;text-align:center}
        .map-sheet{position:absolute;bottom:0;left:0;right:0;background:#fff;border-radius:22px 22px 0 0;box-shadow:0 -4px 24px rgba(15,22,35,.12);transition:transform .35s cubic-bezier(.4,0,.2,1);z-index:1100;touch-action:pan-y;-webkit-overflow-scrolling:touch}
        .map-sheet.collapsed{transform:translateY(calc(100% - 118px))}
        .map-sheet.expanded{transform:translateY(0);max-height:72%;overflow-y:auto}
        .map-sheet.sheet--subscribed.expanded{max-height:88%;min-height:min(52vh,420px);overflow-y:auto}
        .map-sheet.sheet--subscribed.collapsed{transform:translateY(calc(100% - 140px))}
        .sheet-handle{display:flex;justify-content:center;padding:10px 0 4px;cursor:pointer;touch-action:none}
        .sheet-handle div{width:36px;height:4px;background:#e0e4e8;border-radius:2px}
        .sub-banner{margin:0 14px 10px;background:linear-gradient(135deg,#0f1623,#1a2535);border-radius:16px;padding:12px 14px;display:flex;align-items:center;gap:10px}
        .sub-banner-ico{width:36px;height:36px;background:rgba(0,200,150,.2);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0}
        .sub-banner-info{flex:1}
        .sub-banner-ttl{font-size:12px;font-weight:700;color:#fff}
        .sub-banner-sub{font-size:10px;color:rgba(232,237,245,.85);margin-top:1px}
        .sub-banner-badge{background:#00c896;color:#0f1623;font-size:10px;font-weight:700;padding:3px 9px;border-radius:10px;flex-shrink:0}
        .sheet-sec-ttl{font-size:12px;font-weight:700;color:#7a8fa6;padding:0 14px 8px;text-transform:uppercase;letter-spacing:.4px}
        .detail-panel{padding:0 14px}
        .dp-top{display:flex;align-items:flex-start;gap:12px;margin-bottom:14px}
        .dp-img{width:70px;height:70px;border-radius:16px;display:flex;align-items:center;justify-content:center;font-size:30px;flex-shrink:0}
        .dp-info{flex:1}
        .dp-name{font-size:16px;font-weight:700;color:#1a2535}
        .dp-addr{font-size:11px;color:#7a8fa6;margin-top:2px}
        .dp-stars{font-size:12px;color:#f5c542;margin-top:4px}
        .dp-sub-btn{flex:1;background:#0f1623;color:#00c896;border:none;border-radius:12px;padding:10px;font-size:12px;font-weight:700;cursor:pointer}
        .dp-sub-btn.unsub{background:#fde8e8;color:#e05555}
        .dp-cita-btn{flex:1;background:#00c896;color:#0f1623;border:none;border-radius:12px;padding:10px;font-size:12px;font-weight:700;cursor:pointer}
        .dp-sub-row{display:flex;gap:8px;margin-top:8px;align-items:center}
        .dp-stats{display:flex;gap:8px;margin-bottom:12px}
        .dp-stat{flex:1;background:#f0f4f8;border-radius:10px;padding:8px;text-align:center}
        .dp-stat-v{font-size:16px;font-weight:700;color:#1a2535}
        .dp-stat-l{font-size:9px;color:#7a8fa6;margin-top:1px}
        .dp-vets{margin-bottom:12px}
        .dp-vet{display:flex;align-items:center;gap:9px;padding:8px 0;border-bottom:1px solid #e8eef4}
        .dp-vet:last-child{border:none}
        .dp-vet-av{width:34px;height:34px;border-radius:50%;background:linear-gradient(135deg,#0f1623,#00c896);display:flex;align-items:center;justify-content:center;font-size:15px;flex-shrink:0}
        .dp-vet-nm{font-size:12px;font-weight:600;color:#1a2535}
        .dp-vet-sp{font-size:10px;color:#7a8fa6}
        .artemis-ml-pin{background:none;border:none;padding:0;cursor:pointer;display:flex;align-items:center;justify-content:center}
        .artemis-ml-pin.selected .artemis-pin-bubble{transform:scale(1.06)}
        .artemis-leaflet-pin{background:transparent!important;border:none!important}
        .map-watermark{position:absolute;left:12px;bottom:118px;background:rgba(255,255,255,.92);border:1px solid #e8eef4;color:#7a8fa6;border-radius:9999px;padding:4px 9px;font-size:9px;font-weight:600;max-width:70%}
        .map-watermark.wm-explore{bottom:14px}
      `}</style>

      <div className="flex-shrink-0 px-4 pt-3 pb-2 flex items-center gap-3 bg-[#e8edf5] border-b border-[#dde3ec]/60">
        <button
          onClick={() => navigate(-1)}
          className="h-8 w-8 rounded-full flex items-center justify-center text-[#555]"
          type="button"
          aria-label="Volver"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-[17px] font-bold text-[#111] flex-1">Clínicas</h1>
        <button
          type="button"
          onClick={loadClinicas}
          className="h-8 w-8 rounded-full bg-white border border-[#dce3ec] text-[#0f1623] inline-flex items-center justify-center"
          aria-label="Actualizar clínicas"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {error ? (
        <div className="flex-shrink-0 mx-4 mb-2 rounded-xl bg-[#fff3f0] text-[#b84c33] px-3 py-2 text-[11px]">{error}</div>
      ) : null}

      <div className="map-view flex-1 min-h-0">
        {useLeafletFallback ? (
          <div className="vector-map">
            <MapContainer
              center={mapCenter}
              zoom={12}
              scrollWheelZoom
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution={CARTO_ATTRIBUTION}
                url={CARTO_LIGHT_TILE_URL}
                subdomains={CARTO_TILE_SUBDOMAINS}
                maxZoom={20}
              />
              {clinicsForMap.map((clinic) => (
                <Marker
                  key={`fallback-${clinic.id}`}
                  position={[clinic.lat, clinic.lng]}
                  icon={createLeafletPinIcon(clinic, String(clinic.id) === String(selectedClinicId))}
                  eventHandlers={{
                    click: () => {
                      setSelectedClinicId(clinic.id)
                      if (subscribedClinic) setSheetExpanded(true)
                    },
                  }}
                />
              ))}
            </MapContainer>
          </div>
        ) : (
          <div ref={mapContainerRef} className="vector-map" />
        )}

        <div className="map-ui-stack absolute inset-0 z-[1000]">
        <div className={`map-watermark map-ui-interactive ${exploreMode ? 'wm-explore' : ''}`}>
          {useLeafletFallback ? 'Leaflet · Carto' : 'Mapa vectorial · OpenFreeMap'}
        </div>

        {!exploreMode ? (
          <div className="map-legend map-ui-interactive" aria-hidden="true">
            <div className="map-legend-item">
              <span className="map-legend-dot" style={{ background: '#0f1623' }} />
              Clínica activa
            </div>
            <div className="map-legend-item">
              <span className="map-legend-dot" style={{ background: '#00c896' }} />
              Destacada
            </div>
            <div className="map-legend-item">
              <span className="map-legend-dot" style={{ background: '#aab4c0' }} />
              En revisión
            </div>
          </div>
        ) : null}

        <div className="map-search map-ui-interactive">
          <div className="map-srch-box">
            <Search className="w-3.5 h-3.5" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar clínica..."
              className="w-full bg-transparent outline-none text-[13px] text-[#666] placeholder:text-[#aaa]"
            />
          </div>
          {!exploreMode ? (
            <>
              <button
                className={`map-filter-btn ${filterPanelOpen || activeFilters.size > 0 || filterPais ? 'active' : ''}`}
                onClick={() => setFilterPanelOpen((prev) => !prev)}
                type="button"
              >
                <Filter className="w-4 h-4" />
                {activeFilters.size > 0 || filterPais ? (
                  <div className="map-filter-badge">{activeFilters.size + (filterPais ? 1 : 0)}</div>
                ) : null}
              </button>

              <div className={`filter-panel ${filterPanelOpen ? 'open' : ''}`}>
                <div className="fp-title">Filtrar clínicas</div>
                <div className="fp-chips">
                  <button type="button" className={`fchip ${activeFilters.has('suscrito') ? 'on' : ''}`} onClick={() => toggleFilter('suscrito')}>✓ Suscritas</button>
                  <button type="button" className={`fchip ${activeFilters.has('premium') ? 'on-blue' : ''}`} onClick={() => toggleFilter('premium')}>★ Premium</button>
                  <button type="button" className={`fchip ${activeFilters.has('urgencias') ? 'on-orange' : ''}`} onClick={() => toggleFilter('urgencias')}>⚡ Urgencias 24h</button>
                  <button type="button" className={`fchip ${activeFilters.has('cerca') ? 'on-blue' : ''}`} onClick={() => toggleFilter('cerca')}>📍 Cerca de mí</button>
                  <button type="button" className={`fchip ${activeFilters.has('rating') ? 'on-orange' : ''}`} onClick={() => toggleFilter('rating')}>⭐ Top rated</button>
                </div>
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>País</div>
                  <select
                    value={filterPais}
                    onChange={(e) => setFilterPais(e.target.value)}
                    className="w-full rounded-lg border border-[#e3e8ef] bg-white px-2 py-2 text-[13px] text-[#333] outline-none"
                  >
                    <option value="">Todos los países</option>
                    <option value="Uruguay">Uruguay</option>
                    <option value="Argentina">Argentina</option>
                    <option value="Brasil">Brasil</option>
                    <option value="Paraguay">Paraguay</option>
                  </select>
                </div>
                <div className="fp-divider" />
                <button type="button" className="fp-apply" onClick={() => setFilterPanelOpen(false)}>Aplicar filtros</button>
                <button type="button" className="fp-clear" onClick={clearFilters}>Limpiar filtros</button>
              </div>
            </>
          ) : null}
        </div>

        {mapError ? (
          <div className="map-ui-interactive absolute left-14 right-14 top-20 rounded-xl bg-[#fff3f0] px-3 py-2 text-center text-[11px] text-[#b84c33]">
            {mapError}
          </div>
        ) : null}

        {filteredClinics.length > 0 && clinicsForMap.length === 0 ? (
          <div className="map-ui-interactive absolute left-8 right-8 top-[120px] rounded-xl bg-white/95 border border-[#e3e8ef] px-3 py-2 text-center text-[11px] text-[#66798a]">
            Las clínicas filtradas no tienen coordenadas válidas en API.
          </div>
        ) : null}

        {selectedClinic && selectedMapPoint && exploreMode ? (
          <div className="map-floating-popup map-ui-interactive popup-explore">
            <div className="popup-name">{selectedClinic.nombre}</div>
            <div className="popup-meta">
              <MapPin className="w-3 h-3 shrink-0 text-[#7a8fa6]" strokeWidth={2.5} />
              <span>
                {Number(selectedClinic.distancia || 0).toFixed(1)} km · Abierta ahora
              </span>
            </div>
            <div className="popup-btns">
              <button type="button" className="popup-btn popup-btn--primary" onClick={() => openClinicaDetail(selectedClinic)}>
                Ver clínica
              </button>
              <button type="button" className="popup-btn popup-btn--ghost" onClick={() => openDirections(selectedClinic)}>
                Cómo llegar
              </button>
            </div>
            <div className="popup-arrow" />
          </div>
        ) : null}

        </div>

        {subscribedClinic ? (
        <div
          className={`map-sheet sheet--subscribed ${sheetExpanded ? 'expanded' : 'collapsed'}`}
          style={{ transform: `${sheetExpanded ? 'translateY(0)' : 'translateY(calc(100% - 140px))'} translateY(${sheetDragY}px)` }}
        >
          <button
            type="button"
            className="sheet-handle w-full"
            onClick={() => {
              if (suppressSheetToggleClickRef.current) {
                suppressSheetToggleClickRef.current = false
                return
              }
              setSheetExpanded((prev) => !prev)
            }}
            onPointerDown={handleSheetPointerDown}
            onPointerMove={handleSheetPointerMove}
            onPointerUp={finishSheetDrag}
            onPointerCancel={finishSheetDrag}
          >
            <div />
          </button>

          <div className="sub-banner">
            <div className="sub-banner-ico">🏥</div>
            <div className="sub-banner-info">
              <div className="sub-banner-ttl">{subscribedClinic.nombre}</div>
              <div className="sub-banner-sub">Tu clínica suscrita · {subscribedClinic.dir}</div>
            </div>
            <div className="sub-banner-badge">✓ Vigente</div>
          </div>

          {pills.length > 0 ? (
            <div className="filters-row">
              {pills.map((pill) => (
                <button key={pill.key} type="button" className="fchip on" onClick={() => removePill(pill.key)}>
                  {pill.label} ×
                </button>
              ))}
            </div>
          ) : null}

          {activeFilters.size > 0 ? (
            <div className="sheet-sec-ttl">Filtros activos · {activeFilters.size}</div>
          ) : null}

          {selectedClinic ? (
            <div className="detail-panel pb-4">
              <div className="dp-top">
                <div
                  className="dp-img"
                  style={{
                    background:
                      selectedClinic.pinState === 'review'
                        ? 'linear-gradient(135deg,#aab4c0,#8a94a0)'
                        : selectedClinic.pinState === 'featured'
                          ? 'linear-gradient(135deg,#00c896,#00a67a)'
                          : 'linear-gradient(135deg,#0f1623,#1a2535)',
                  }}
                >
                  {selectedClinic.emoji}
                </div>
                <div className="dp-info">
                  <div className="dp-name">{selectedClinic.nombre}</div>
                  <div className="dp-addr">
                    📍 {selectedClinic.dir}
                    {selectedClinic.pais ? <span style={{ color: '#888' }}> · {selectedClinic.pais}</span> : null}
                  </div>
                  <div className="dp-stars">
                    {stars(selectedClinic.rating)} <span style={{ color: '#888', fontSize: '11px' }}>{selectedClinic.rating.toFixed(1)}</span>
                  </div>
                  {selectedClinic.suscrito ? (
                    <div style={{ marginTop: '5px', fontSize: '11px', fontWeight: 700, color: '#00c896' }}>✓ Estás suscrito a esta clínica</div>
                  ) : null}
                </div>
              </div>

              <div className="dp-stats">
                <div className="dp-stat"><div className="dp-stat-v" style={{ color: '#f5c542' }}>{selectedClinic.rating.toFixed(1)}</div><div className="dp-stat-l">Rating</div></div>
                <div className="dp-stat"><div className="dp-stat-v">{vetsLoading ? '…' : String(selectedClinicVets.length || 0)}</div><div className="dp-stat-l">Veterinarios</div></div>
                <div className="dp-stat"><div className="dp-stat-v" style={{ color: selectedClinic.urgencias ? '#00c896' : '#aab4c0' }}>{selectedClinic.urgencias ? '24h' : 'No'}</div><div className="dp-stat-l">Urgencias</div></div>
              </div>

              <div className="dp-sub-row">
                {isDueno ? (
                  <button
                    type="button"
                    disabled={busyClinicId === String(selectedClinic.id)}
                    className={`dp-sub-btn ${selectedClinic.suscrito ? 'unsub' : ''} disabled:opacity-60`}
                    onClick={() => handleSubscriptionToggle(selectedClinic.id)}
                  >
                    {busyClinicId === String(selectedClinic.id) ? 'Procesando...' : selectedClinic.suscrito ? '✗ Darse de baja' : '+ Suscribirse'}
                  </button>
                ) : null}
                <button type="button" className="dp-cita-btn" onClick={() => navigate(`/citas?nuevo=1&clinica=${selectedClinic.id}`)}>
                  📅 Pedir cita
                </button>
              </div>

              <div style={{ height: '10px' }} />
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: '8px' }}>
                Veterinarios
              </div>

              <div className="dp-vets">
                {vetsLoading ? (
                  <div className="text-[11px] text-[#888] py-2">Cargando veterinarios...</div>
                ) : vetsToDisplay.length === 0 ? (
                  <div className="text-[11px] text-[#888] py-2">Sin veterinarios cargados para esta clínica.</div>
                ) : (
                  vetsToDisplay.map((vet, idx) => {
                    const name = vet.nombre_completo || vet.nombreCompleto || vet.nombre || `${vet.nombres || ''} ${vet.apellidos || ''}`.trim() || `Veterinario ${idx + 1}`
                    const especialidad = vet.especialidad || vet.especialidades || 'Medicina general'
                    const rating = parseNumber(vet.rating || vet.calificacion, 4.7)
                    return (
                      <div key={vet.id_veterinario || vet.id || `${name}-${idx}`} className="dp-vet">
                        <div className="dp-vet-av">{idx % 2 === 0 ? '👨‍⚕️' : '👩‍⚕️'}</div>
                        <div>
                          <div className="dp-vet-nm">{name}</div>
                          <div className="dp-vet-sp">{especialidad}</div>
                          <div style={{ color: '#f5c542', fontSize: '11px' }}>
                            {stars(rating)} <span style={{ color: '#888', fontSize: '10px' }}>{rating.toFixed(1)}</span>
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          ) : null}
        </div>
        ) : null}
      </div>
    </div>
  )
}
